import { memo, useEffect, useRef } from 'react';
import type { VttCue } from '@/features/content/utils/vttCues';
import { formatCueTime } from '@/features/content/utils/vttCues';
import { canLookUp } from '@/features/content/services/dictionaryService';
import type { ReadingPrefs } from '@/features/content/services/readingPrefsService';
import { cx } from '@/shared/ui';

export type InteractiveTextProps = {
  cues: VttCue[];
  activeCueId: string | null;
  prefs: ReadingPrefs;
  onSeek: (seconds: number) => void;
  onWordSelect: (word: string) => void;
};

/*
 * One line, memoised. Splitting a cue on whitespace and shape-testing every
 * token is cheap once — but the page re-renders on every playback tick, and
 * without the memo a ten-minute transcript re-split all ~300 lines (thousands
 * of regex runs, thousands of reconciled <button>s) each time. With it, a cue
 * boundary re-renders exactly the two lines whose `active` flag changed, and
 * everything else compares equal: cues are parsed once, and ContentPage keeps
 * the handlers' identities stable for precisely this reason.
 */
const TranscriptLine = memo(function TranscriptLine({
  cue,
  active,
  activeRef,
  onSeek,
  onWordSelect,
}: {
  cue: VttCue;
  active: boolean;
  activeRef: React.RefObject<HTMLLIElement | null>;
  onSeek: (seconds: number) => void;
  onWordSelect: (word: string) => void;
}) {
  return (
    <li
      ref={active ? activeRef : undefined}
      className={cx('transcript__line', active && 'is-active')}
      aria-current={active ? 'true' : undefined}
    >
      <button
        type="button"
        className="transcript__time"
        aria-label={`Jump to ${formatCueTime(cue.start)}`}
        onClick={() => onSeek(cue.start)}
      >
        {formatCueTime(cue.start)}
      </button>

      <p className="transcript__text">
        {cue.text.split(/(\s+)/).map((chunk, index) => {
          if (!chunk.trim()) return chunk;
          if (!canLookUp(chunk)) return chunk;
          return (
            <button
              type="button"
              key={`${cue.id}-${index}`}
              className="transcript__word"
              onClick={() => onWordSelect(chunk)}
            >
              {chunk}
            </button>
          );
        })}
      </p>
    </li>
  );
});

/**
 * The timestamped transcript. Clicking a line seeks the player; clicking a word
 * looks it up.
 *
 * Which words are tappable is a shape test rather than a dictionary membership
 * test — see `canLookUp`. Against a general dictionary that means very nearly
 * all of them, which is why `.transcript__word` holds its underline back until
 * hover: marking every word permanently would leave the line unreadable.
 *
 * Words are split on whitespace rather than tokenised, so punctuation stays
 * attached to its word and the line reads normally — `normalizeWord` in the
 * dictionary strips it for the lookup.
 */
export const InteractiveText = memo(function InteractiveText({
  cues,
  activeCueId,
  prefs,
  onSeek,
  onWordSelect,
}: InteractiveTextProps) {
  const activeRef = useRef<HTMLLIElement | null>(null);

  // Follow along as the video plays, but only within the panel — `nearest`
  // rather than `center` so the page itself does not jump under the viewer.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [activeCueId]);

  if (cues.length === 0) {
    return (
      <p className="transcript__empty">
        No transcript yet — captions appear once this video finishes rendering.
      </p>
    );
  }

  return (
    <ol
      className={cx(
        'transcript',
        `transcript--${prefs.textSize}`,
        `transcript--face-${prefs.typeface}`,
        `transcript--active-${prefs.activeLine}`,
      )}
    >
      {cues.map((cue) => (
        <TranscriptLine
          key={cue.id}
          cue={cue}
          active={cue.id === activeCueId}
          activeRef={activeRef}
          onSeek={onSeek}
          onWordSelect={onWordSelect}
        />
      ))}
    </ol>
  );
});
